// Page de connexion au gabarit des applications ODICE : logo centré, surtitre de l'application,
// carte centrée avec le bouton Microsoft puis, en dessous, la connexion administrateur.

import { LoginSchema } from '@cockpit/shared';
import { Button, ErrorState, Field, Input, Spinner } from '@cockpit/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/Layout.js';
import { api, ApiError } from '../lib/api.js';
import { useMe } from '../lib/queries.js';

const SSO_ERRORS: Record<string, string> = {
  sso: 'La connexion Microsoft a échoué. Réessayez ou contactez l’administrateur.',
  session: 'Votre session de connexion a expiré. Recommencez.',
  compte:
    'Cette adresse correspond à un compte local de l’application, qui ne peut pas être utilisé avec Microsoft. Contactez l’administrateur.',
  email: 'Votre compte Microsoft ne fournit pas d’adresse e-mail. Contactez l’administrateur.',
};

export function LoginPage() {
  const [params] = useSearchParams();
  const retour = params.get('retour') ?? '/';
  const me = useMe();
  const qc = useQueryClient();
  const providers = useQuery({
    queryKey: ['providers'],
    queryFn: () => api<{ entra: boolean; local: boolean }>('/api/auth/providers'),
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(SSO_ERRORS[params.get('erreur') ?? ''] ?? null);
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  if (me.data) return <Navigate to={retour} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = LoginSchema.safeParse({ email, password });
    if (!parsed.success) return setError('Saisissez une adresse e-mail valide et votre mot de passe.');
    setBusy(true);
    setError(null);
    try {
      const u = await api('/api/auth/login', { method: 'POST', body: parsed.data });
      qc.setQueryData(['me'], u);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connexion impossible.');
    } finally {
      setBusy(false);
    }
  };

  const loaded = !providers.isLoading;
  const entra = !!providers.data?.entra;
  // Sans SSO configuré, seul le compte administrateur est disponible : son formulaire est affiché d'emblée.
  const formOpen = loaded && (showAdmin || !entra);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <header className="flex flex-col items-center gap-3 text-center">
        <Logo className="h-11 w-auto sm:h-[51px]" />
        <p className="font-display text-[12px] uppercase tracking-[0.25em] text-ink-3 sm:text-[13px]">
          Cockpit de pilotage
        </p>
      </header>

      <main className="mt-8 w-full max-w-[448px] rounded-md border border-line bg-surface p-6 shadow-od-1 sm:p-8">
        <h1 className="font-display text-2xl font-bold leading-tight text-ink">Connexion</h1>
        <p className="mt-2 text-[15px] leading-5 text-ink-2">
          Suivez la performance des entités, l’atterrissage et les chantiers à traiter.
        </p>

        {error && (
          <div className="mt-6">
            <ErrorState title="Connexion impossible">{error}</ErrorState>
          </div>
        )}

        {!loaded && <Spinner className="mt-8" />}

        {entra && (
          <a
            href={`/api/auth/entra?returnTo=${encodeURIComponent(retour)}`}
            className="mt-6 flex h-14 w-full items-center justify-center rounded-sm bg-action font-display text-[17px] font-semibold text-on-action no-underline transition-colors duration-base hover:bg-action-hover hover:text-on-action"
          >
            Se connecter avec Microsoft
          </a>
        )}

        {loaded && (
          <div className={entra ? 'mt-4 border-t border-line-soft pt-4' : 'mt-6'}>
            {entra && (
              <button
                type="button"
                aria-expanded={showAdmin}
                aria-controls="connexion-admin"
                onClick={() => setShowAdmin((v) => !v)}
                className="inline-flex items-center gap-2 text-[14.5px] font-semibold text-navy-600 transition-colors duration-fast hover:text-ink"
              >
                <KeyRound size={16} strokeWidth={1.75} aria-hidden />
                Connexion administrateur
              </button>
            )}

            {formOpen && (
              <form
                id="connexion-admin"
                onSubmit={submit}
                noValidate
                className={entra ? 'mt-4 flex flex-col gap-4' : 'flex flex-col gap-4'}
              >
                {!entra && (
                  <p className="rounded-sm bg-surface-2 px-3 py-2 text-[13.5px] text-ink-2">
                    La connexion Microsoft n’est pas encore activée : seul le compte administrateur est
                    disponible.
                  </p>
                )}
                <Field label="Adresse e-mail">
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Mot de passe">
                  {(id) => (
                    <Input
                      id={id}
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  )}
                </Field>
                <Button
                  type="submit"
                  disabled={busy}
                  variant={entra ? 'secondary' : 'primary'}
                  className="mt-1 h-11"
                >
                  {busy ? 'Connexion…' : 'Se connecter'}
                </Button>
              </form>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
